import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const emptyForm = {
  roomName: '',
  stableId: '',
  capacity: '',
};

const StableRoomList = () => {
  const { t, i18n } = useTranslation();
  const [rooms, setRooms] = useState([]);
  const [stables, setStables] = useState([]);
  const [editRoomId, setEditRoomId] = useState(null);
  const [deleteRoomId, setDeleteRoomId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const userId = localStorage.getItem('userId');
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchRooms = useCallback(async () => {
    const response = await api.get(`${apiUrl}/rooms`);
    setRooms(Array.isArray(response.data) ? response.data : []);
  }, []);

  const fetchStables = useCallback(async () => {
    const response = await api.get(`${apiUrl}/stables`);
    const nextStables = Array.isArray(response.data) ? response.data : [];
    setStables(nextStables);
    setForm((current) => ({
      ...current,
      stableId: current.stableId || nextStables[0]?.stable_id || '',
    }));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([fetchRooms(), fetchStables()]);
    } catch (err) {
      setError(err?.response?.data?.error || t('stable_rooms_load_error'));
    } finally {
      setLoading(false);
    }
  }, [fetchRooms, fetchStables, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRooms = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rooms;
    return rooms.filter((room) => (
      String(room.room_name || '').toLowerCase().includes(term)
      || String(room.stable_name || '').toLowerCase().includes(term)
    ));
  }, [rooms, search]);
  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedRooms,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredRooms, [search]);

  const resetForm = useCallback(() => {
    setEditRoomId(null);
    setIsAddDialogOpen(false);
    setForm({
      ...emptyForm,
      stableId: stables[0]?.stable_id || '',
    });
  }, [stables]);

  const openAddDialog = useCallback(() => {
    setMessage('');
    setError('');
    setForm({
      ...emptyForm,
      stableId: stables[0]?.stable_id || '',
    });
    setIsAddDialogOpen(true);
  }, [stables]);

  const openEditDialog = useCallback((room) => {
    setMessage('');
    setError('');
    setEditRoomId(room.room_id);
    setForm({
      roomName: room.room_name || '',
      stableId: room.stable_id || '',
      capacity: room.capacity ?? '',
    });
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  const validateForm = useCallback(() => {
    if (!form.stableId) {
      setError(t('stable_required'));
      return false;
    }
    if (!form.roomName.trim()) {
      setError(t('room_name_required'));
      return false;
    }
    if (form.capacity === '' || !Number.isFinite(Number(form.capacity)) || Number(form.capacity) <= 0) {
      setError(t('positive_numbers_required'));
      return false;
    }
    return true;
  }, [form.capacity, form.roomName, form.stableId, t]);

  const getRoomErrorMessage = useCallback((err, fallback) => {
    const key = err?.response?.data?.error;
    if (key && String(key).startsWith('errors.')) {
      return t(key);
    }
    return fallback;
  }, [t]);

  const saveRoom = useCallback(async () => {
    if (!validateForm()) return;
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      room_name: form.roomName.trim(),
      stable_id: form.stableId,
      capacity: form.capacity,
      price_per_day: 0,
    };
    try {
      if (editRoomId) {
        await api.put(`${apiUrl}/rooms/update-room/${editRoomId}`, {
          ...payload,
          modified_by: userId,
        });
        setMessage(t('room_updated_successfully'));
      } else {
        await api.post(`${apiUrl}/rooms/insert-new-room`, {
          ...payload,
          created_by: userId,
        });
        setMessage(t('room_added_successfully'));
      }
      resetForm();
      await fetchRooms();
    } catch (err) {
      setError(getRoomErrorMessage(err, editRoomId ? t('room_update_error') : t('room_add_error')));
    } finally {
      setSaving(false);
    }
  }, [editRoomId, fetchRooms, form, getRoomErrorMessage, resetForm, t, userId, validateForm]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`${apiUrl}/rooms/delete-room/${deleteRoomId}`, {
        data: { deleted_by: userId },
      });
      setDeleteRoomId(null);
      setMessage(t('room_deleted_successfully'));
      await fetchRooms();
    } catch (err) {
      setError(getRoomErrorMessage(err, t('room_delete_error')));
    } finally {
      setSaving(false);
    }
  }, [deleteRoomId, fetchRooms, getRoomErrorMessage, t, userId]);

  return (
    <Box
      className="smart-page-container"
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <SettingsPageHeader
        title={t('stable_rooms')}
        subtitle={t('stable_rooms_hint')}
        count={rooms.length}
        actions={(
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {t('add_room')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('search_rooms')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 360 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell>{t('room_name')}</TableCell>
                <TableCell>{t('stable_name')}</TableCell>
                <TableCell align="center">{t('capacity')}</TableCell>
                <TableCell align="center" sx={{ width: 150 }}>{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRooms.map((room) => (
                <TableRow key={room.room_id} hover>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 180 }}>{room.room_name}</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>{room.stable_name}</TableCell>
                  <TableCell align="center">{room.capacity}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit_room')}>
                        <IconButton onClick={() => openEditDialog(room)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete_room')}>
                        <IconButton onClick={() => setDeleteRoomId(room.room_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredRooms.length && !loading && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('no_rooms_found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination
          count={filteredRooms.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Dialog open={isAddDialogOpen || Boolean(editRoomId)} onClose={resetForm} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{editRoomId ? t('edit_room') : t('add_room')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('stable_name')}</InputLabel>
              <Select label={t('stable_name')} value={form.stableId || ''} onChange={(event) => updateField('stableId', event.target.value)}>
                {stables.map((stable) => (
                  <MenuItem key={stable.stable_id} value={stable.stable_id}>
                    {stable.stable_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField fullWidth label={t('room_name')} value={form.roomName} onChange={(event) => updateField('roomName', event.target.value)} />
            <TextField fullWidth label={t('capacity')} type="number" value={form.capacity} onChange={(event) => updateField('capacity', event.target.value)} inputProps={{ min: 0 }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetForm} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={saveRoom} variant="contained" disabled={saving} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteRoomId)} onClose={() => setDeleteRoomId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('delete_room_confirmation')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteRoomId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {t('cancel')}
          </Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StableRoomList;
